import React, { createContext, PropsWithChildren, useContext } from 'react'
import SalesForceProvider, { SalesForceContext } from './SalesForce/SalesForceProvider'
import Five9Provider, { Five9Context } from './Five9/Five9Provider'

const SUPPORTED_CHAT_PROVIDERS = ['five9', 'salesforce']

type ChatSupportProviderType = (typeof SUPPORTED_CHAT_PROVIDERS)[number]

/**
 *
 */
export type ChatSupportContextValue = {
    chatReady: boolean,
    openChat: () => unknown,
    hideChatButton: () => unknown,
    showChatButton: () => unknown
}

const defaultChatSupportContext = {
    chatReady: false,
    openChat: () => undefined,
    hideChatButton: () => undefined,
    showChatButton: () => undefined
}

/**
 *
 */
export const ChatSupportContext = createContext<ChatSupportContextValue>(defaultChatSupportContext)

type ChatSupportProviderProps = PropsWithChildren<{
    provider: ChatSupportProviderType
}>

const SalesForceChatSupportProvider = ({ children }: PropsWithChildren<unknown>): JSX.Element => (
    <SalesForceProvider>
        <SalesForceContext.Consumer>
            {contextValue => (
                <ChatSupportContext.Provider value={contextValue}>
                    {children}
                </ChatSupportContext.Provider>
            )}
        </SalesForceContext.Consumer>
    </SalesForceProvider>
)

const Five9ChatSupportProvider = ({ children }: PropsWithChildren<unknown>): JSX.Element => (
    <Five9Provider>
        <Five9Context.Consumer>
            {contextValue => (
                <ChatSupportContext.Provider value={contextValue || defaultChatSupportContext}>
                    {children}
                </ChatSupportContext.Provider>
            )}
        </Five9Context.Consumer>
    </Five9Provider>
)

/**
 *
 */
export const ChatSupportProvider = ({
    provider,
    children
}: ChatSupportProviderProps): JSX.Element => {
    if (!SUPPORTED_CHAT_PROVIDERS.includes(provider)) {
        console.error('Unsupported chat support provider. Using default chat support provider.')
    }
    if (provider === 'salesforce') {
        return <SalesForceChatSupportProvider>{children}</SalesForceChatSupportProvider>
    }

    return <Five9ChatSupportProvider>{children}</Five9ChatSupportProvider>
}

/**
 *
 */
export const useChatSupportContext = (): ChatSupportContextValue => {
    const chatSupportContext = useContext(ChatSupportContext)
    if (chatSupportContext === undefined) {
        throw new Error('useChatSupportContext must be used inside ChatSupportProvider tags')
    }

    return { ...chatSupportContext }
}

export default ChatSupportProvider
